import {
  type Color,
  Group,
  InstancedMesh,
  MathUtils,
  Matrix4,
  MeshBasicMaterial,
  PlaneGeometry,
  Quaternion,
  Vector3,
} from 'three'
import {gsap} from 'gsap'
import {colors, rectDatas_fromLeft_small, type RectData} from './data'

const RECT_ASPECT = 1
const TOTAL_COLS_SMALL = 6
const FRAGMENT_COLS = 4
const FRAGMENT_ROWS = 4

const FRAGMENT_OVERLAP = 1.003
const FRAGMENT_GROUP_CHANCE = 0.7
const ASSEMBLY_DELAY = 0.0
const MAX_FRAGMENT_START_DELAY = 3
const RANDOM_TIMING_WEIGHT = 0.2
const COLOR_STEP_DURATION = 0.25
const TRANSITION_COLOR_PALETTE = Object.values(colors).filter(
  color => color !== colors.orange && color !== colors.brown && color !== colors.black,
)

const TOTAL_TIMING_COLUMN_COUNT = (Math.max(...rectDatas_fromLeft_small.map(data => data.colIndex)) + 1) * FRAGMENT_COLS

type Fragment = {
  colIndex: number
  rowIndex: number
  colSpan: 1 | 2
  rowSpan: 1 | 2
  delay: number
  transitionColor: Color
}

type FragmentedRect = {
  data: RectData
  mesh: InstancedMesh<PlaneGeometry, MeshBasicMaterial>
  fragments: Fragment[]
  fragmentCols: number
  fragmentRows: number
  width: number
  height: number
}

export default class Blocks {
  group = new Group()

  readonly #geometry = new PlaneGeometry(1, 1)
  readonly #materials = new Set<MeshBasicMaterial>()
  readonly #rects: FragmentedRect[]
  readonly #matrix = new Matrix4()
  readonly #position = new Vector3()
  readonly #rotation = new Quaternion()
  readonly #scale = new Vector3()
  readonly #animationState = {elapsed: 0}
  readonly #reduceMotion: boolean
  readonly #assemblyDuration: number
  readonly #onOpeningComplete?: () => void
  #animationTween: gsap.core.Tween | null = null
  #hasStartedAnimation = false

  constructor(reduceMotion: boolean, onOpeningComplete?: () => void) {
    this.#reduceMotion = reduceMotion
    this.#onOpeningComplete = onOpeningComplete
    this.#rects = this.#createRects(rectDatas_fromLeft_small, FRAGMENT_COLS, FRAGMENT_ROWS)
    this.#assemblyDuration = this.#getAssemblyDuration()
  }

  resize(viewportWidth: number, viewportHeight: number) {
    const smallHeight = viewportHeight / TOTAL_COLS_SMALL
    const smallWidth = smallHeight * RECT_ASPECT
    const smallGridBottom = -viewportHeight / 2
    const leftEdge = -viewportWidth / 2

    for (const rect of this.#rects) {
      rect.width = smallWidth
      rect.height = smallHeight
      rect.mesh.position.set(
        leftEdge + (rect.data.colIndex + 0.5) * smallWidth,
        smallGridBottom + (rect.data.rowIndex + 0.5) * smallHeight,
        0,
      )
    }

    this.#updateFragmentMatrices()
    this.#startAnimation()
  }

  dispose() {
    this.#animationTween?.kill()
    this.#animationTween = null
    this.group.clear()
    this.#geometry.dispose()

    for (const material of this.#materials) {
      material.dispose()
    }
    this.#materials.clear()
  }

  #createRects(rectDatas: RectData[], fragmentCols: number, fragmentRows: number) {
    return rectDatas.map(data => {
      const fragments = this.#createFragments(data, fragmentCols, fragmentRows, data.colIndex * fragmentCols)
      const material = this.#createMaterial()
      const mesh = new InstancedMesh(this.#geometry, material, fragments.length)

      mesh.frustumCulled = false
      mesh.renderOrder = -1
      this.group.add(mesh)

      return {
        data,
        mesh,
        fragments,
        fragmentCols,
        fragmentRows,
        width: 0,
        height: 0,
      }
    })
  }

  #createFragments(data: RectData, fragmentCols: number, fragmentRows: number, timingColumnOffset: number) {
    const fragments: Fragment[] = []
    const occupied = Array.from({length: fragmentRows}, () => Array<boolean>(fragmentCols).fill(false))
    const rectSeed = data.color.getHex() + data.colIndex * 101 + data.rowIndex * 211
    const rectRandomProgress = this.#random(rectSeed + 3)

    for (let rowIndex = 0; rowIndex < fragmentRows; rowIndex += 1) {
      const row = occupied[rowIndex]
      if (!row) {
        throw new Error(`Missing fragment row ${rowIndex}`)
      }

      for (let colIndex = 0; colIndex < fragmentCols; colIndex += 1) {
        if (row[colIndex]) {
          continue
        }

        const fragmentSeed = rectSeed + colIndex * 17 + rowIndex * 37
        const globalColumnIndex = timingColumnOffset + colIndex
        const nextRow = occupied[rowIndex + 1]
        const canCreateGroup =
          colIndex + 1 < fragmentCols &&
          rowIndex + 1 < fragmentRows &&
          nextRow !== undefined &&
          !row[colIndex + 1] &&
          !nextRow[colIndex] &&
          !nextRow[colIndex + 1]
        const isGroup = canCreateGroup && this.#random(fragmentSeed + 4) < FRAGMENT_GROUP_CHANCE
        const colSpan = isGroup ? 2 : 1
        const rowSpan = isGroup ? 2 : 1
        const randomProgress = (rectRandomProgress + this.#random(fragmentSeed + 1)) / 2

        for (const occupiedRow of occupied.slice(rowIndex, rowIndex + rowSpan)) {
          occupiedRow.fill(true, colIndex, colIndex + colSpan)
        }

        fragments.push({
          colIndex,
          rowIndex,
          colSpan,
          rowSpan,
          delay: this.#getFragmentDelay(globalColumnIndex, randomProgress),
          transitionColor: this.#getTransitionColor(data.color, fragmentSeed),
        })
      }
    }

    return fragments
  }

  #updateFragmentMatrices() {
    for (const rect of this.#rects) {
      const fragmentWidth = rect.width / rect.fragmentCols
      const fragmentHeight = rect.height / rect.fragmentRows

      for (const [index, fragment] of rect.fragments.entries()) {
        const fragmentScale = this.#animationState.elapsed >= fragment.delay ? 1 : 0
        const targetX = (fragment.colIndex + fragment.colSpan / 2 - rect.fragmentCols / 2) * fragmentWidth
        const targetY = (fragment.rowIndex + fragment.rowSpan / 2 - rect.fragmentRows / 2) * fragmentHeight
        const finalWidth = fragmentWidth * fragment.colSpan * FRAGMENT_OVERLAP
        const finalHeight = fragmentHeight * fragment.rowSpan * FRAGMENT_OVERLAP

        this.#position.set(targetX, targetY, 0)
        this.#rotation.identity()
        this.#scale.set(finalWidth * fragmentScale, finalHeight * fragmentScale, 1)
        this.#matrix.compose(this.#position, this.#rotation, this.#scale)
        rect.mesh.setMatrixAt(index, this.#matrix)
        rect.mesh.setColorAt(
          index,
          this.#animationState.elapsed < fragment.delay + COLOR_STEP_DURATION
            ? fragment.transitionColor
            : rect.data.color,
        )
      }

      rect.mesh.instanceMatrix.needsUpdate = true
      if (rect.mesh.instanceColor) {
        rect.mesh.instanceColor.needsUpdate = true
      }
    }
  }

  #startAnimation() {
    if (this.#hasStartedAnimation) {
      return
    }
    this.#hasStartedAnimation = true

    if (this.#reduceMotion) {
      this.#animationState.elapsed = this.#assemblyDuration
      this.#updateFragmentMatrices()
      this.#onOpeningComplete?.()
      return
    }

    this.#animationTween = gsap.to(this.#animationState, {
      elapsed: this.#assemblyDuration,
      duration: this.#assemblyDuration,
      delay: ASSEMBLY_DELAY,
      ease: 'none',
      onUpdate: () => this.#updateFragmentMatrices(),
      onComplete: () => {
        this.#animationState.elapsed = this.#assemblyDuration
        this.#updateFragmentMatrices()
        this.#animationTween = null
        this.#onOpeningComplete?.()
      },
    })
  }

  #getAssemblyDuration() {
    let duration = 0

    for (const rect of this.#rects) {
      for (const fragment of rect.fragments) {
        duration = Math.max(duration, fragment.delay + COLOR_STEP_DURATION)
      }
    }

    return duration
  }

  #getFragmentDelay(columnIndex: number, randomProgress: number) {
    const horizontalProgress = columnIndex / (TOTAL_TIMING_COLUMN_COUNT - 1)
    const timingProgress = MathUtils.lerp(horizontalProgress, randomProgress, RANDOM_TIMING_WEIGHT)

    return timingProgress * MAX_FRAGMENT_START_DELAY
  }

  #random(seed: number) {
    const value = Math.sin(seed) * 43758.5453

    return value - Math.floor(value)
  }

  #getTransitionColor(originalColor: Color, seed: number) {
    const availableColors = TRANSITION_COLOR_PALETTE.filter(color => color.getHex() !== originalColor.getHex())
    const colorIndex = Math.floor(this.#random(seed + 5) * availableColors.length)

    const transitionColor = availableColors[colorIndex]
    if (!transitionColor) {
      throw new Error(`Missing transition color ${colorIndex}`)
    }

    return transitionColor
  }

  #createMaterial() {
    const material = new MeshBasicMaterial({
      color: 0xffffff,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      // wireframe: true,
    })
    this.#materials.add(material)

    return material
  }
}
